import React, { useRef } from 'react';
import { X } from 'lucide-react';
import {
  clipSourceEnd,
  insertSpeedZone,
  layOutClips,
  normaliseSpeedZones,
  setClipSpeedZones,
  speedCurve,
  speedOutputSeconds,
  speedSourceAt,
  CLIP_SPEED_PRESETS,
  MAX_CLIP_SPEED,
  MIN_CLIP_SPEED,
  MIN_SPEED_ZONE_SECONDS,
  type EditorState,
  type LaidOutClip,
  type SpeedZone,
} from '@rehub/propertycut-timeline';
import type { PropertyCutAsset } from '../../types';
import type { EditorSelection } from './model';

/** Space kept above the fastest and below the slowest speed. */
const PAD = 12;
/** A section made by dragging lasts about this long on screen. */
const NEW_SECTION_SECONDS = 2;
const GRID_SPEEDS = [8, 4, 2, 0.5, 0.25];
/** 1x stays on the centre line; each half of the row covers its own range. */
const OCTAVES_UP = Math.log2(MAX_CLIP_SPEED);
const OCTAVES_DOWN = -Math.log2(MIN_CLIP_SPEED);

function speedToY(speed: number, height: number): number {
  const half = height / 2;
  const octaves = Math.log2(speed);
  return half - (octaves / (octaves >= 0 ? OCTAVES_UP : OCTAVES_DOWN)) * (half - PAD);
}

function yToSpeed(y: number, height: number): number {
  const half = height / 2;
  const fraction = (half - y) / Math.max(1, half - PAD);
  const octaves = fraction * (fraction >= 0 ? OCTAVES_UP : OCTAVES_DOWN);
  return Math.min(MAX_CLIP_SPEED, Math.max(MIN_CLIP_SPEED, 2 ** octaves));
}

/** Snaps to 1x and the presets when close, otherwise to a tidy step. */
function snapSpeed(speed: number): number {
  if (Math.abs(Math.log2(speed)) < 0.08) return 1;
  for (const preset of CLIP_SPEED_PRESETS) {
    if (Math.abs(Math.log2(speed / preset)) < 0.05) return preset;
  }
  if (speed < 1) return Math.round(speed * 20) / 20;
  return speed < 4 ? Math.round(speed * 10) / 10 : Math.round(speed * 2) / 2;
}

function speedLabel(speed: number): string {
  return `${Number(speed.toFixed(2))}×`;
}

/** Pixels from the lane's top-left corner. */
interface Point {
  x: number;
  y: number;
}

interface SpeedLaneProps {
  state: EditorState;
  assets: PropertyCutAsset[];
  zoom: number;
  height: number;
  selected: EditorSelection;
  onSelect: (selection: EditorSelection) => void;
  onPreview: (state: EditorState) => void;
  onCommit: () => void;
  onEdit: (recipe: (state: EditorState) => EditorState) => void;
}

/**
 * Speed curve for every video clip, around a 1x centre line. Double-click to
 * add a ramp, drag its label sideways to move it or up and down to change
 * speed, and drag the round handles to resize it.
 */
export default function SpeedLane(props: SpeedLaneProps) {
  const laneRef = useRef<HTMLDivElement>(null);
  const height = Math.max(40, props.height - 1);
  const laid = layOutClips(props.state.clips);

  const pointer = (ev: PointerEvent | React.PointerEvent): Point => {
    const rect = laneRef.current?.getBoundingClientRect();
    return rect ? { x: ev.clientX - rect.left, y: ev.clientY - rect.top } : { x: 0, y: 0 };
  };

  const drag = (
    event: React.PointerEvent,
    clipId: string,
    onMove: (origin: EditorState, at: Point, begin: Point) => EditorState | null,
  ) => {
    event.stopPropagation();
    event.preventDefault();
    const origin = props.state;
    const begin = pointer(event);
    const startX = event.clientX;
    const startY = event.clientY;
    let moved = false;
    const move = (ev: PointerEvent) => {
      if (!moved && Math.hypot(ev.clientX - startX, ev.clientY - startY) < 4) return;
      moved = true;
      const next = onMove(origin, pointer(ev), begin);
      if (next) props.onPreview(next);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (moved) props.onCommit();
      else props.onSelect({ type: 'clip', id: clipId });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <div ref={laneRef} className="relative h-full select-none">
      {GRID_SPEEDS.map((speed) => (
        <div
          key={speed}
          className="absolute inset-x-0 border-t border-dashed border-slate-800 pointer-events-none"
          style={{ top: speedToY(speed, height) }}
        />
      ))}
      <div className="absolute inset-x-0 border-t border-slate-600/70 pointer-events-none" style={{ top: height / 2 }} />
      {laid.clips.map((clip) => {
        const asset = props.assets.find((item) => item.id === clip.assetId);
        if (clip.blank || asset?.kind !== 'video') return null;
        return (
          <ClipSpeed
            key={clip.id}
            clip={clip}
            sourceSeconds={asset.duration_seconds}
            zoom={props.zoom}
            height={height}
            selected={props.selected?.type === 'clip' && props.selected.id === clip.id}
            drag={drag}
            onEdit={props.onEdit}
          />
        );
      })}
    </div>
  );
}

function ClipSpeed({
  clip,
  sourceSeconds,
  zoom,
  height,
  selected,
  drag,
  onEdit,
}: {
  clip: LaidOutClip;
  sourceSeconds: number | null;
  zoom: number;
  height: number;
  selected: boolean;
  drag: (
    event: React.PointerEvent,
    clipId: string,
    onMove: (origin: EditorState, at: Point, begin: Point) => EditorState | null,
  ) => void;
  onEdit: SpeedLaneProps['onEdit'];
}) {
  const trim = clip.trimStart;
  const end = clipSourceEnd(clip);
  const width = Math.max(clip.duration * zoom, 6);
  const mid = height / 2;
  const left = clip.start * zoom;
  const zones = normaliseSpeedZones(clip.speedZones).filter((zone) => zone.end > trim && zone.start < end);
  const outputAt = (source: number) => speedOutputSeconds(clip.speedZones, trim, source) * zoom;

  const points = speedCurve(clip.speedZones, trim, end).map((p) => `${p.t * zoom},${speedToY(p.speed, height)}`);
  const line = points.length ? `M${points.join(' L')}` : '';
  const area = points.length ? `${line} L${width},${mid} L0,${mid} Z` : '';
  const above = `speed-above-${clip.id}`;
  const below = `speed-below-${clip.id}`;

  /** Clip of the gesture's starting state, so pointer maths uses one layout throughout. */
  const originClip = (origin: EditorState) => layOutClips(origin.clips).clips.find((item) => item.id === clip.id) ?? null;
  const localSeconds = (origin: LaidOutClip, x: number) => Math.max(0, Math.min(origin.duration, x / zoom - origin.start));

  const createSection = (event: React.PointerEvent) =>
    drag(event, clip.id, (origin, at, begin) => {
      const from = originClip(origin);
      if (!from) return null;
      if (Math.abs(at.y - begin.y) < 4) return null;
      const fromEnd = clipSourceEnd(from);
      const centre = speedSourceAt(from.speedZones, from.trimStart, localSeconds(from, begin.x));
      const speed = snapSpeed(yToSpeed(at.y, height));
      const length = Math.min(NEW_SECTION_SECONDS * Math.max(1, speed), fromEnd - from.trimStart);
      const start = Math.max(from.trimStart, Math.min(centre - length / 2, fromEnd - length));
      const zone: SpeedZone = { start, end: start + length, speed };
      return insertSpeedZone(origin, clip.id, zone, sourceSeconds);
    });

  const dragZone = (event: React.PointerEvent, index: number) => {
    let mode: 'move' | 'speed' | null = null;
    drag(event, clip.id, (origin, at, begin) => {
      const from = originClip(origin);
      if (!from) return null;
      const list = normaliseSpeedZones(from.speedZones);
      const target = zones[index];
      if (!target) return null;
      const at0 = list.findIndex((zone) => zone.start === target.start && zone.end === target.end);
      if (at0 < 0) return null;
      const dx = at.x - begin.x;
      const dy = at.y - begin.y;
      if (!mode) mode = Math.abs(dx) > Math.abs(dy) ? 'move' : 'speed';
      const current = list[at0];
      if (mode === 'speed') {
        const next = list.map((zone, i) => (i === at0 ? { ...zone, speed: snapSpeed(yToSpeed(at.y, height)) } : zone));
        return setClipSpeedZones(origin, clip.id, next, sourceSeconds);
      }
      const fromEnd = clipSourceEnd(from);
      const span = current.end - current.start;
      const floor = Math.max(from.trimStart, list[at0 - 1]?.end ?? 0);
      const ceiling = Math.min(fromEnd, list[at0 + 1]?.start ?? Infinity);
      const start = Math.max(floor, Math.min(current.start + (dx / zoom) * current.speed, ceiling - span));
      return setClipSpeedZones(
        origin,
        clip.id,
        list.map((zone, i) => (i === at0 ? { ...current, start, end: start + span } : zone)),
        sourceSeconds,
      );
    });
  };

  const addRampAt = (clientX: number, clipLeft: number) => {
    const local = Math.max(0, Math.min(clip.duration, (clientX - clipLeft) / zoom));
    const centre = speedSourceAt(clip.speedZones, trim, local);
    const speed = 2;
    const length = Math.min(NEW_SECTION_SECONDS * speed, end - trim);
    if (length < MIN_SPEED_ZONE_SECONDS) return;
    const start = Math.max(trim, Math.min(centre - length / 2, end - length));
    onEdit((state) => insertSpeedZone(state, clip.id, { start, end: start + length, speed }, sourceSeconds));
  };

  const dragEdge = (event: React.PointerEvent, index: number, edge: 'start' | 'end') =>
    drag(event, clip.id, (origin, at) => {
      const from = originClip(origin);
      if (!from) return null;
      const list = normaliseSpeedZones(from.speedZones);
      const target = zones[index];
      const at0 = list.findIndex((zone) => zone.start === target.start && zone.end === target.end);
      if (at0 < 0) return null;
      const zone = list[at0];
      const fromEnd = clipSourceEnd(from);
      const local = localSeconds(from, at.x);
      let moved: SpeedZone;
      if (edge === 'start') {
        const floor = Math.max(from.trimStart, list[at0 - 1]?.end ?? 0);
        const source = speedSourceAt(from.speedZones, from.trimStart, local);
        moved = { ...zone, start: Math.max(floor, Math.min(source, zone.end - MIN_SPEED_ZONE_SECONDS)) };
      } else {
        const visibleStart = Math.max(zone.start, from.trimStart);
        const startAt = speedOutputSeconds(from.speedZones, from.trimStart, visibleStart);
        const ceiling = Math.min(fromEnd, list[at0 + 1]?.start ?? Infinity);
        const source = visibleStart + (local - startAt) * zone.speed;
        moved = { ...zone, end: Math.min(ceiling, Math.max(source, visibleStart + MIN_SPEED_ZONE_SECONDS)) };
      }
      return setClipSpeedZones(origin, clip.id, list.map((z, i) => (i === at0 ? moved : z)), sourceSeconds);
    });

  const reset = (index: number) => {
    const target = zones[index];
    onEdit((state) => {
      const current = state.clips.find((item) => item.id === clip.id);
      if (!current) return state;
      const kept = normaliseSpeedZones(current.speedZones).filter((z) => z.start !== target.start || z.end !== target.end);
      return setClipSpeedZones(state, clip.id, kept, sourceSeconds);
    });
  };

  return (
    <div
      className={`absolute top-0 bottom-0 rounded-md border ${selected ? 'border-teal-300/80 bg-teal-400/[0.06]' : 'border-slate-700/80 bg-slate-900/40'} cursor-crosshair`}
      style={{ left, width }}
      title="Double-click to add a speed ramp. Drag a ramp sideways to move it."
      onPointerDown={createSection}
      onDoubleClick={(event) => {
        event.stopPropagation();
        addRampAt(event.clientX, event.currentTarget.getBoundingClientRect().left);
      }}
    >
      <svg className="absolute inset-0 pointer-events-none" width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <defs>
          <clipPath id={above}><rect x={0} y={0} width={width} height={mid} /></clipPath>
          <clipPath id={below}><rect x={0} y={mid} width={width} height={height - mid} /></clipPath>
        </defs>
        <path d={area} fill="rgba(252, 211, 77, 0.28)" clipPath={`url(#${above})`} />
        <path d={area} fill="rgba(56, 189, 248, 0.28)" clipPath={`url(#${below})`} />
        <path d={line} fill="none" stroke="#5eead4" strokeWidth={2} strokeLinejoin="round" />
      </svg>
      {width > 44 && (
        <>
          <span className="absolute left-1 top-0.5 text-[9px] text-slate-500 pointer-events-none">Faster</span>
          <span className="absolute left-1 bottom-0.5 text-[9px] text-slate-500 pointer-events-none">Slower</span>
        </>
      )}
      {width > 90 && height >= 80 &&
        [...GRID_SPEEDS, 1].map((speed) => (
          <span
            key={speed}
            className="absolute right-1 -translate-y-1/2 text-[9px] leading-none text-slate-600 pointer-events-none"
            style={{ top: speedToY(speed, height) }}
          >
            {speedLabel(speed)}
          </span>
        ))}
      {zones.map((zone, index) => {
        const x0 = outputAt(Math.max(zone.start, trim));
        const x1 = outputAt(Math.min(zone.end, end));
        const y = speedToY(zone.speed, height);
        const labelTop = zone.speed >= 1 ? Math.min(y + 8, mid - 14) : Math.max(y - 22, mid + 2);
        return (
          <React.Fragment key={`${zone.start}-${index}`}>
            <div
              role="slider"
              aria-label="Section speed"
              aria-valuenow={zone.speed}
              aria-valuemin={MIN_CLIP_SPEED}
              aria-valuemax={MAX_CLIP_SPEED}
              title="Drag sideways to move this ramp. Drag up or down to change the speed."
              className="absolute z-10 cursor-grab group"
              style={{ left: x0 + 4, width: Math.max(6, x1 - x0 - 8), top: y - 8, height: 16 }}
              onPointerDown={(event) => dragZone(event, index)}
              onDoubleClick={(event) => event.stopPropagation()}
            >
              <span className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-teal-200/0 group-hover:bg-teal-200/70" />
            </div>
            <span
              className={`absolute z-20 flex items-center gap-0.5 rounded pl-1 text-[10px] font-semibold cursor-grab active:cursor-grabbing ${zone.speed > 1 ? 'bg-amber-300 text-amber-950' : 'bg-sky-300 text-sky-950'}`}
              style={{ left: (x0 + x1) / 2, top: labelTop, transform: 'translateX(-50%)' }}
              title="Drag sideways to move this ramp. Drag up or down to change the speed."
              onPointerDown={(event) => dragZone(event, index)}
              onDoubleClick={(event) => event.stopPropagation()}
            >
                {speedLabel(zone.speed)}
                <button
                  type="button"
                  aria-label="Remove this speed ramp"
                  title="Remove this speed ramp"
                  className="grid h-4 w-4 place-items-center rounded-r cursor-pointer hover:bg-black/20"
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    reset(index);
                  }}
                >
                  <X className="h-2.5 w-2.5" strokeWidth={3} />
                </button>
              </span>
            <RampDot x={x0} y={y} onPointerDown={(event) => dragEdge(event, index, 'start')} />
            <RampDot x={x1} y={y} onPointerDown={(event) => dragEdge(event, index, 'end')} />
          </React.Fragment>
        );
      })}
      {zones.length > 0 && width > 150 && (
        <button
          type="button"
          className="absolute right-8 top-0.5 z-20 rounded px-1 text-[9px] text-slate-400 hover:bg-rose-500/15 hover:text-rose-200"
          title="Remove every speed ramp on this clip"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            onEdit((state) => setClipSpeedZones(state, clip.id, [], sourceSeconds));
          }}
        >
          Remove all
        </button>
      )}
      {width > 150 && (
        <span className="absolute inset-x-8 text-center text-[10px] text-slate-500 pointer-events-none" style={{ top: zones.length === 0 ? mid + 4 : height - 14 }}>
          {zones.length === 0 ? 'Double-click to add a ramp' : 'Drag a ramp sideways to move it · drag the dots to resize'}
        </span>
      )}
    </div>
  );
}

function RampDot({ x, y, onPointerDown }: { x: number; y: number; onPointerDown: (event: React.PointerEvent) => void }) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Drag to move this end of the speed ramp"
      title="Drag left or right to move this end"
      className="absolute z-30 h-6 w-6 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize"
      style={{ left: x, top: y }}
      onPointerDown={onPointerDown}
      onDoubleClick={(event) => event.stopPropagation()}
    >
      <span className="absolute left-1/2 top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-teal-300 shadow" />
    </div>
  );
}
